import { useCallback, useContext, useEffect, useState, useRef } from "react";
import CardOption from "./cards";

import {
  ACHResponse,
  PaymentsService,
  CreditCardResponse,
  ParentLimitedResponse,
  StudentLimitedResponse,
  PaymentMethodType,
  ParentsService,
} from "client/openapi";
import { APIResponse, PageStatus } from "types";
import Notifications from "util/notifications";
import BankOption from "./bank";
import EmailOption from "./emails";
import ExternalPaymentMethodInput from "./otherPaymentMethod";

import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";

export default function PaymentMethods({
  selectable,
  deletable,
  showOptionToAddExternalPaymentMethodSource,
  setPaymentMethod,
  setPaymentMethodType,
  studentIfInitiatedByAdmin,
  parentIfInitiatedByAdmin,
  initiatedByStudentOrParentDirectly,
  defaultSelectedId,
}: {
  selectable?: boolean;
  deletable?: boolean;
  showOptionToAddExternalPaymentMethodSource?: boolean;
  setPaymentMethod?: (
    payment_method: CreditCardResponse | ACHResponse | string | undefined
  ) => void;
  setPaymentMethodType?: (
    paymentMethodType: PaymentMethodType | undefined
  ) => void;
  studentIfInitiatedByAdmin?: StudentLimitedResponse;
  parentIfInitiatedByAdmin?: ParentLimitedResponse;
  initiatedByStudentOrParentDirectly?: boolean;
  defaultSelectedId?: string;
}) {
  const [status, setStatus] = useState<PageStatus>();
  const [error, setError] = useState<APIResponse>();

  const [userCards, setUserCards] = useState<CreditCardResponse[]>();
  const [userBanks, setUserBanks] = useState<ACHResponse[]>();
  const [invoiceEmails, setInvoiceEmails] = useState<string[]>();

  const { account } = useContext(OrgRolesAndAccountContext);

  const org_id =
    studentIfInitiatedByAdmin?.org_id || parentIfInitiatedByAdmin?.org_id;

  const [
    viewExternalPaymentMethodSourceInput,
    setViewExternalPaymentMethodSourceInput,
  ] = useState(false);

  // Ref to track radio buttons
  const radioButtonsRef = useRef<HTMLInputElement[]>([]);

  // Function to uncheck all radio buttons
  const uncheckAllRadioButtons = () => {
    radioButtonsRef.current.forEach((radio) => {
      if (radio) {
        radio.checked = false;
      }
    });
  };

  // Function to register a radio button
  const registerRadioButton = (element: HTMLInputElement | null) => {
    if (element) {
      radioButtonsRef.current.push(element);
    }
  };

  // Function to handle radio button selection
  const handleRadioSelection = (
    paymentMethod: CreditCardResponse | ACHResponse | string,
    paymentMethodType: PaymentMethodType
  ) => {
    // Hide external payment method input when radio is selected
    setViewExternalPaymentMethodSourceInput(false);

    // Set the payment method
    if (setPaymentMethod) {
      setPaymentMethod(paymentMethod);
    }
    if (setPaymentMethodType) {
      setPaymentMethodType(paymentMethodType);
    }
  };

  // Function to handle external payment method input
  const handleExternalPaymentMethod = (description: string | undefined) => {
    if (description) {
      // Clear any selected radio buttons
      if (setPaymentMethod) {
        setPaymentMethod(description);
      }
      if (setPaymentMethodType) {
        setPaymentMethodType(PaymentMethodType.EXTERNAL_PAYMENT_METHOD);
      }
    } else {
      // Handle undefined case
      if (setPaymentMethod) {
        setPaymentMethod(undefined);
      }
      if (setPaymentMethodType) {
        setPaymentMethodType(undefined);
      }
    }
  };

  const getAndSetCards = useCallback(async () => {
    setStatus(PageStatus.LOADING);

    try {
      let cards;
      if (initiatedByStudentOrParentDirectly) {
        cards = await PaymentsService.getAccountCards();
      } else if (studentIfInitiatedByAdmin) {
        cards = await PaymentsService.getStudentCards({
          studentId: studentIfInitiatedByAdmin.id,
        });
      } else if (parentIfInitiatedByAdmin) {
        cards = await PaymentsService.getParentCards({
          parentId: parentIfInitiatedByAdmin.id,
        });
      }
      setUserCards(cards);
      setStatus(PageStatus.SUCCESS);
    } catch (error: any) {
      setError(error);
      setStatus(PageStatus.ERROR);
      console.error(`Error (#${error.status}): ${error.message}`);
    }
  }, [
    initiatedByStudentOrParentDirectly,
    parentIfInitiatedByAdmin,
    studentIfInitiatedByAdmin,
  ]);

  const getAndSetBanks = useCallback(async () => {
    setStatus(PageStatus.LOADING);

    try {
      let banks;
      if (initiatedByStudentOrParentDirectly) {
        banks = await PaymentsService.getAccountAch();
      } else if (studentIfInitiatedByAdmin) {
        banks = await PaymentsService.getStudentAchAccounts({
          studentId: studentIfInitiatedByAdmin.id,
        });
      } else if (parentIfInitiatedByAdmin) {
        banks = await PaymentsService.getParentAchAccounts({
          parentId: parentIfInitiatedByAdmin.id,
        });
      }
      setUserBanks(banks);
      setStatus(PageStatus.SUCCESS);
    } catch (error: any) {
      setError(error);
      setStatus(PageStatus.ERROR);
      console.error(`Error (#${error.status}): ${error.message}`);
    }
  }, [
    initiatedByStudentOrParentDirectly,
    parentIfInitiatedByAdmin,
    studentIfInitiatedByAdmin,
  ]);

  const getAndSetEmails = useCallback(async () => {
    if (parentIfInitiatedByAdmin) {
      setInvoiceEmails(
        parentIfInitiatedByAdmin.email ? [parentIfInitiatedByAdmin.email] : []
      );
    } else if (studentIfInitiatedByAdmin) {
      setStatus(PageStatus.LOADING);
      ParentsService.getParentsByStudent({
        studentId: studentIfInitiatedByAdmin.id,
      })
        .then((parents) => {
          const parentEmails = parents
            .filter((p) => p.email && p.email.includes("@"))
            .map((p) => p.email as string);

          const studentEmail = studentIfInitiatedByAdmin.email;
          const allEmails = studentEmail
            ? Array.from(new Set([...parentEmails, studentEmail]))
            : Array.from(new Set(parentEmails));

          setInvoiceEmails(allEmails);
          setStatus(PageStatus.SUCCESS);
        })
        .catch((error) => {
          setError(error);
          setStatus(PageStatus.ERROR);
          console.error(`Error (#${error.status}): ${error.message}`);
        });
    }
  }, [parentIfInitiatedByAdmin, studentIfInitiatedByAdmin]);

  useEffect(() => {
    if (account) {
      getAndSetCards();
      getAndSetBanks();
    }
    if (account && (parentIfInitiatedByAdmin || studentIfInitiatedByAdmin)) {
      getAndSetEmails();
    }
  }, [account, parentIfInitiatedByAdmin, studentIfInitiatedByAdmin]);

  useEffect(() => {
    error &&
      Notifications.error(error?.message || "An unexpected error occurred.");
  }, [error]);

  return (
    <>
      <div className="rounded border mt-2 p-4 border-gray-300">
        <table className="w-full border-spacing-0.5">
          <thead>
            <tr className="text-left text-xs text-gray-500">
              <th className="pb-2 whitespace-nowrap font-bold">
                Charge to Card{" "}
                <span style={{ marginLeft: "290px" }}>Expires On</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {userCards && userCards.length > 0 ? (
              userCards.map((card) => (
                <CardOption
                  key={card.payment_method_id}
                  orgId={org_id}
                  card={card}
                  deletable={deletable}
                  selectable={selectable}
                  onRadioSelect={handleRadioSelection}
                  radioRef={registerRadioButton}
                  defaultSelected={defaultSelectedId === card.payment_method_id}
                />
              ))
            ) : (
              <tr>
                <td>No cards on file</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="rounded border mt-2 p-4 border-gray-300">
        <table className="w-full border-spacing-0.5">
          <thead>
            <tr className="text-left text-xs text-gray-500">
              <th className="pb-2 whitespace-nowrap font-bold">
                Charge to Bank
              </th>
            </tr>
          </thead>
          <tbody>
            {userBanks && userBanks.length > 0 ? (
              userBanks.map((bank) => (
                <BankOption
                  key={bank.payment_method_id}
                  orgId={org_id}
                  bank={bank}
                  deletable={deletable}
                  selectable={selectable}
                  onRadioSelect={handleRadioSelection}
                  radioRef={registerRadioButton}
                  defaultSelected={defaultSelectedId === bank.payment_method_id}
                />
              ))
            ) : (
              <tr>
                <td>No banks on file</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="rounded border mt-2 p-4 border-gray-300">
        <table className="w-full border-spacing-0.5">
          <thead>
            <tr className="text-left text-xs text-gray-500">
              <th className="pb-2 whitespace-nowrap font-bold">
                or, you can send payment a link to
              </th>
            </tr>
          </thead>
          <tbody>
            {invoiceEmails && invoiceEmails.length > 0 ? (
              invoiceEmails.map((email, index) => (
                <EmailOption
                  key={`${email}-${index}`}
                  email={email}
                  selectable={selectable}
                  onRadioSelect={handleRadioSelection}
                  radioRef={registerRadioButton}
                  defaultSelected={defaultSelectedId === email}
                />
              ))
            ) : (
              <tr>
                <td>No emails on file</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showOptionToAddExternalPaymentMethodSource && (
        <div>
          {!viewExternalPaymentMethodSourceInput && (
            <button
              className={`mt-4 underline text-xs font-light header font-semibold text-gray-600`}
              onClick={() => {
                setViewExternalPaymentMethodSourceInput(true);
                uncheckAllRadioButtons();
                // Clear any selected payment method
                if (setPaymentMethod) {
                  setPaymentMethod(undefined);
                }
                if (setPaymentMethodType) {
                  setPaymentMethodType(undefined);
                }
              }}
            >
              or, log that this transaction has already been paid externally
            </button>
          )}

          {viewExternalPaymentMethodSourceInput && (
            <>
              <button
                className={`mt-4 underline text-xs font-light header font-semibold text-gray-600`}
                onClick={() => {
                  setViewExternalPaymentMethodSourceInput(false);
                  if (setPaymentMethod) {
                    setPaymentMethod(undefined);
                  }
                  if (setPaymentMethodType) {
                    setPaymentMethodType(undefined);
                  }
                }}
              >
                cancel
              </button>

              <ExternalPaymentMethodInput
                setPaymentMethod={handleExternalPaymentMethod}
                setPaymentMethodType={setPaymentMethodType || (() => {})}
              />
            </>
          )}
        </div>
      )}
    </>
  );
}
